import { ref, watch, onUnmounted } from 'vue';
import { useUserStore } from '@/stores/user';
import { useSnackbar } from '@/composables/useSnackbar';
import { WEBSOCKET_ENDPOINT } from '@/constants';
import i18next from '@/config/i18n';
import eventBus from '@/eventBus';
import analytics, { ANALYTICS_EVENTS } from '@/services/analytics';
import { getStoredToken } from '@/utils/authToken';

// Trailing debounce timers — module-level since only App.vue instantiates this.
// subscription_usage_updated can burst (N jobs finish simultaneously → N estornos).
// queue_update batches the last payload so rapid pushes collapse into one write.
let _usageUpdateTimer = null;
let _queueUpdateTimer = null;
let _pendingQueueData = null;
const USAGE_UPDATE_DEBOUNCE_MS = 2000;
const QUEUE_UPDATE_DEBOUNCE_MS = 500;

/**
 * Bulletproof WebSocket composable for SynapSee Studio.
 *
 * Features:
 *  - Exponential backoff reconnection (1s → 2s → 4s → ... → 30s cap)
 *  - Heartbeat ping every 30s to prevent idle disconnections
 *  - Page visibility handling (reconnects when tab becomes visible)
 *  - Protection against double reconnect from onerror+onclose race
 *  - State reconciliation event on reconnect so consumers can refetch
 *  - Clean teardown on unmount (no leaked timers or sockets)
 */
export function useWebSocket() {
    const userStore = useUserStore();
    const { showSnackbar } = useSnackbar();

    // --- State ---
    const socket = ref(null);
    const isConnected = ref(false);
    const shouldBeConnected = ref(false);

    // --- Reconnect Config ---
    const BASE_RECONNECT_DELAY = 1000;      // 1s initial
    const MAX_RECONNECT_DELAY = 30000;       // 30s cap
    const MAX_RECONNECT_ATTEMPTS = 8;

    let reconnectAttempts = 0;
    let reconnectTimer = null;
    let isReconnecting = false;              // guards against double attemptReconnect

    // --- Heartbeat Config ---
    const HEARTBEAT_INTERVAL = 30000;        // 30s
    let heartbeatTimer = null;

    // =========================================================================
    // Heartbeat
    // =========================================================================

    function startHeartbeat() {
        stopHeartbeat();
        heartbeatTimer = setInterval(() => {
            if (socket.value && socket.value.readyState === WebSocket.OPEN) {
                try {
                    socket.value.send(JSON.stringify({ type: 'ping' }));
                } catch {
                    // Send failed — connection likely broken, onclose will handle it
                }
            }
        }, HEARTBEAT_INTERVAL);
    }

    function stopHeartbeat() {
        if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
            heartbeatTimer = null;
        }
    }

    // =========================================================================
    // Message Handling
    // =========================================================================

    function handleMessage(event) {
        try {
            const data = JSON.parse(event.data);

            // Ignore pong responses from server
            if (data.type === 'pong') return;

            console.log('WS: Received message:', data);

            // Global handlers (previously in App.vue)
            // Trilha legal: o broadcast carrega o doc_type publicado. Payload antigo
            // (sem o campo) vale para a trilha atual, que é o default do store.
            if (data.type === 'terms_updated' && data.version
                && (!data.doc_type || data.doc_type === userStore.termsDocType)) {
                userStore.setTermsUpdateRequired(data.version);
            }

            if (data.type === 'queue_update' && Array.isArray(data.queue)) {
                _pendingQueueData = data.queue;
                clearTimeout(_queueUpdateTimer);
                _queueUpdateTimer = setTimeout(() => {
                    _queueUpdateTimer = null;
                    if (_pendingQueueData) {
                        userStore.processingQueue = _pendingQueueData;
                        _pendingQueueData = null;
                    }
                }, QUEUE_UPDATE_DEBOUNCE_MS);
            }

            if (data.type === 'subscription_usage_updated') {
                clearTimeout(_usageUpdateTimer);
                _usageUpdateTimer = setTimeout(() => {
                    _usageUpdateTimer = null;
                    userStore.fetchSubscriptionUsage();
                }, USAGE_UPDATE_DEBOUNCE_MS);
            }

            // SYN-435: telemetria do "frontend recebeu". Pareada com o emit do
            // backend, permite calcular a taxa de WS-drop por análise (quantos
            // status_update saíram do backend e não chegaram aqui).
            if (data.type === 'status_update' && data.experiment_id) {
                analytics.track(ANALYTICS_EVENTS.WS_STATUS_UPDATE_RECEIVED, {
                    experiment_id: data.experiment_id,
                    analysis_status: data.analysis_status ?? null,
                    optimizer_status: data.optimizer_status ?? null,
                    terminal: ['completed', 'failed'].includes(data.analysis_status) ||
                              ['completed', 'failed'].includes(data.optimizer_status),
                });
            }

            // Broadcast to all consumers via eventBus
            eventBus.emit('websocket-message', data);
        } catch (e) {
            console.error('WS: Error processing message:', e, 'Raw data:', event.data);
        }
    }

    // =========================================================================
    // Connection
    // =========================================================================

    function connect() {
        // SYN-2755 — token guardado (o localStorage é compartilhado entre abas), não o
        // `userStore.token` em memória: uma aba aberta há mais de 24h guardava o
        // token vencido e, ao reconectar, levava 1008 e deslogava todas as abas.
        // Read the shared stored token, never the stale in-memory copy.
        const token = getStoredToken();
        if (!shouldBeConnected.value || !userStore.isAuthenticated || !token) return;
        if (socket.value && socket.value.readyState !== WebSocket.CLOSED) return;

        const wsEndpoint = WEBSOCKET_ENDPOINT;
        /* istanbul ignore next -- WEBSOCKET_ENDPOINT always set in test and production; guard is a startup safety net */
        if (!wsEndpoint) {
            console.error('WS: VITE_WEBSOCKET_ENDPOINT not configured.');
            return;
        }

        const wsUrl = `${wsEndpoint}?token=${token}`;

        try {
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                console.log('WS: Connected.');
                isConnected.value = true;
                reconnectAttempts = 0;
                isReconnecting = false;
                clearReconnectTimer();
                startHeartbeat();

                // CRITICAL: Emit reconnect event so consumers can refetch state
                // to recover any messages lost during disconnection.
                eventBus.emit('websocket-reconnected');
            };

            ws.onmessage = handleMessage;

            ws.onclose = (event) => {
                isConnected.value = false;
                stopHeartbeat();
                socket.value = null;

                console.warn(
                    `WS: Closed — code=${event.code}, reason="${event.reason || '(none)'}", wasClean=${event.wasClean}`
                );

                // Auth failure — don't reconnect, force logout
                if (event.code === 1008) {
                    // SYN-2755 — outra aba já gravou um token novo: o recusado era
                    // velho. Reconecta com o novo em vez de deslogar (o logout
                    // apagaria do storage a sessão boa das outras abas).
                    // Another tab stored a newer token: retry with it, don't log out.
                    const storedToken = getStoredToken();
                    if (storedToken && storedToken !== token) {
                        scheduleReconnect();
                        return;
                    }
                    console.warn('WS: Connection closed due to policy violation (invalid token).');
                    shouldBeConnected.value = false;
                    userStore.logout({ message: i18next.t('errors.unauthorized') });
                    return;
                }

                // Intentional close by us — don't reconnect
                if (!shouldBeConnected.value) {
                    resetReconnectState();
                    return;
                }

                // All other closes (clean or not): attempt reconnect.
                // Servers do clean closes during deploys — we still want to reconnect.
                scheduleReconnect();
            };

            ws.onerror = (error) => {
                console.error('WS: Error:', error);
                // Don't call scheduleReconnect here — onclose always fires after onerror,
                // so reconnection is handled there. This prevents the double-reconnect race.
            };

            socket.value = ws;
        } catch (err) {
            console.error('WS: Failed to create WebSocket:', err);
            socket.value = null;
            scheduleReconnect();
        }
    }

    // =========================================================================
    // Reconnection (Exponential Backoff)
    // =========================================================================

    function scheduleReconnect() {
        if (!shouldBeConnected.value) return;
        if (isReconnecting) return;  // Guard: already have a pending reconnect

        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            showSnackbar('Desconectado do servidor de notificações. Recarregue a página para reconectar.', 'warning');
            resetReconnectState();
            return;
        }

        isReconnecting = true;
        const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
        reconnectAttempts++;
        console.log(`WS: Reconnecting in ${delay / 1000}s (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);

        clearReconnectTimer();
        reconnectTimer = setTimeout(() => {
            isReconnecting = false;
            connect();
        }, delay);
    }

    function clearReconnectTimer() {
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
    }

    function resetReconnectState() {
        clearReconnectTimer();
        reconnectAttempts = 0;
        isReconnecting = false;
    }

    // =========================================================================
    // Visibility Change Handler
    // =========================================================================

    function handleVisibilityChange() {
        if (document.visibilityState === 'visible' && shouldBeConnected.value) {
            // Tab became visible — check if socket is still alive
            if (!socket.value || socket.value.readyState === WebSocket.CLOSED || socket.value.readyState === WebSocket.CLOSING) {
                console.log('WS: Tab became visible, connection lost. Reconnecting immediately...');
                reconnectAttempts = 0; // Reset attempts for a fresh start
                isReconnecting = false;
                clearReconnectTimer();
                connect();
            }
        }
    }

    // =========================================================================
    // Public API
    // =========================================================================

    function open() {
        shouldBeConnected.value = true;
        connect();
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    function close() {
        shouldBeConnected.value = false;
        stopHeartbeat();
        resetReconnectState();
        document.removeEventListener('visibilitychange', handleVisibilityChange);

        if (socket.value) {
            // Null out onclose to prevent triggering reconnect on intentional close
            socket.value.onclose = null;
            socket.value.onerror = null;
            socket.value.close(1000, 'Intentional close');
            socket.value = null;
        }
        isConnected.value = false;
    }

    // =========================================================================
    // Lifecycle: react to auth changes
    // =========================================================================

    watch(() => userStore.isAuthenticated, (isAuth) => {
        if (isAuth) {
            open();
        } else {
            close();
        }
    });

    // Auto-cleanup on unmount of the component that calls useWebSocket()
    onUnmounted(() => {
        close();
    });

    return {
        isConnected,
        open,
        close,
    };
}
